// Página ProductDetail - Detalle completo de un producto
// Muestra toda la información del producto: imágenes, descripción, variantes, especificaciones
// Diseño actualizado basado en mockup Pine & Co

import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useProduct, useProducts } from '../hooks/useFirestore'
import { useCategories } from '../hooks/useCategories'
import { useCart } from '../hooks/useCart'
import { useBusinessRulesContext } from '../hooks/useBusinessRulesContext'
import { formatCurrency, calculateProductFinalPrice } from '../utils/helpers'
import { getCategory } from '../services/categories.service'
import { getHelpSettings } from '../services/helpSettings.service'
import { getImageUrl } from '../services/cloudinary.service'
import { buildWhatsAppLinkAR } from '../utils/whatsapp'
import Toast from '../components/Toast'

const buildEmbedUrl = (url) => {
  const raw = String(url || '').trim()
  if (!raw) return null
  if (raw.includes('youtube.com/embed/')) return raw
  const ytMatch = raw.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]+)/)
  if (ytMatch?.[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`
  const igMatch = raw.match(/instagram\.com\/(reel|p|tv)\/([A-Za-z0-9_-]+)/)
  if (igMatch?.[1] && igMatch?.[2]) return `https://www.instagram.com/${igMatch[1]}/${igMatch[2]}/embed`
  const ttMatch = raw.match(/tiktok\.com\/.+\/video\/(\d+)/)
  if (ttMatch?.[1]) return `https://www.tiktok.com/embed/v2/${ttMatch[1]}`
  if (raw.includes('facebook.com')) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(raw)}&show_text=false&width=500`
  }
  return null
}

const resolveImageSource = (image) => {
  if (!image) return null
  if (typeof image === 'string') return image
  if (typeof image === 'object') return image.fileId || image.url || image.thumbnailUrl || null
  return null
}

const getVariantImages = (product, transformations) => {
  const images = []
  const variants = Array.isArray(product?.variants) ? product.variants : []
  variants.forEach(variant => {
    const variantImages = Array.isArray(variant?.images) ? variant.images : []
    variantImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      if (!images.includes(url)) images.push(url)
    })
  })
  if (images.length === 0) {
    const fallbackImages = Array.isArray(product?.images) ? product.images : []
    fallbackImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      if (!images.includes(url)) images.push(url)
    })
  }
  return images
}

/**
 * Página ProductDetail
 * Muestra el detalle completo de un producto con todas sus características
 */
function ProductDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  
  // Obtener el producto actual
  const { product, loading, error } = useProduct(id)
  
  // Obtener categorías para breadcrumbs
  const { categories } = useCategories()
  
  // Hook del carrito
  const { addToCart, getCartItemCount } = useCart()

  const { rules } = useBusinessRulesContext()
  const outOfStockPolicy = rules?.outOfStockPolicy || { visibility: 'show', purchaseBehavior: 'block_purchase', inquiryChannel: 'whatsapp', labelText: 'Sin stock' }
  
  // Estado para variantes seleccionadas
  const [selectedVariants, setSelectedVariants] = useState({})
  const [quantity, setQuantity] = useState(1)
  const [notification, setNotification] = useState(null)
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [helpSettings, setHelpSettings] = useState(null)
  
  // Estado para la categoría completa (con costos)
  const [fullCategory, setFullCategory] = useState(null)

  // Obtener la categoría del producto para breadcrumbs
  const productCategory = useMemo(() => {
    if (!product || !categories.length) return null
    return categories.find(cat => cat.id === product.categoryId || cat.name === product.category)
  }, [product, categories])
  
  // Cargar categoría completa con costos
  useEffect(() => {
    const loadFullCategory = async () => {
      if (product?.categoryId) {
        try {
          const category = await getCategory(product.categoryId)
          setFullCategory(category)
        } catch (error) {
          console.error('Error cargando categoría completa:', error)
          setFullCategory(null)
        }
      } else {
        setFullCategory(null)
      }
    }
    loadFullCategory()
  }, [product?.categoryId])

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const data = await getHelpSettings()
        if (mounted) setHelpSettings(data)
      } catch {
        if (mounted) setHelpSettings(null)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  // Agrupar variantes por tipo
  const variantsByType = useMemo(() => {
    if (!product || !product.variants || product.variants.length === 0) {
      return {}
    }

    const grouped = {}
    product.variants.forEach(variant => {
      if (!grouped[variant.type]) {
        grouped[variant.type] = []
      }
      grouped[variant.type].push(variant)
    })

    return grouped
  }, [product])

  const selectedVariant = useMemo(() => {
    const values = Object.values(selectedVariants).filter(Boolean)
    if (values.length === 0) return null
    if (values.length === 1) return values[0]
    return values[values.length - 1]
  }, [selectedVariants])

  // Calcular precio final (precio base + costos + variantes seleccionadas)
  const finalPrice = useMemo(() => {
    if (!product) return 0

    // Calcular precio base con costos incluidos
    const basePriceWithCosts = calculateProductFinalPrice(product, fullCategory)

    // Sumar precios de variantes seleccionadas
    let finalPriceWithVariants = basePriceWithCosts
    Object.values(selectedVariants).forEach(variant => {
      if (variant && variant.price !== null && variant.price !== undefined) {
        finalPriceWithVariants += variant.price
      }
    })

    return finalPriceWithVariants
  }, [product, fullCategory, selectedVariants])

  // Calcular stock disponible (stock base o stock de variante)
  const availableStock = useMemo(() => {
    if (!product) return 0

    // Si hay variantes seleccionadas, usar el stock de la variante
    if (selectedVariant && selectedVariant.stock !== null && selectedVariant.stock !== undefined) {
      return selectedVariant.stock
    }

    return product.stock || 0
  }, [product, selectedVariant])

  useEffect(() => {
    if (!product) return
    const entries = Object.entries(variantsByType)
    if (!entries.length) return
    setSelectedVariants(prev => {
      const next = { ...prev }
      let changed = false
      for (const [type, variants] of entries) {
        if (next[type]) continue
        if (Array.isArray(variants) && variants.length === 1) {
          next[type] = variants[0]
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [product, variantsByType])

  useEffect(() => {
    setActiveImageIndex(0)
  }, [selectedVariant, product?.id])

  // Manejar selección de variante
  const handleVariantSelect = (type, variant) => {
    setSelectedVariants(prev => ({
      ...prev,
      [type]: variant
    }))
    // Resetear cantidad si cambia la variante
    setQuantity(1)
  }

  // Manejar cambio de cantidad
  const handleQuantityChange = (delta) => {
    setQuantity(prev => {
      const newQuantity = prev + delta
      if (newQuantity < 1) return 1
      if (outOfStockPolicy.purchaseBehavior !== 'allow_purchase' && newQuantity > availableStock) return availableStock
      return newQuantity
    })
  }

  // Manejar agregar al carrito
  const handleAddToCart = () => {
    // Validar que todas las variantes requeridas estén seleccionadas
    const requiredVariantTypes = Object.keys(variantsByType)
    const missingVariants = requiredVariantTypes.filter(type => !selectedVariants[type])

    if (missingVariants.length > 0) {
      setNotification({
        type: 'error',
        message: `Por favor selecciona: ${missingVariants.join(', ')}`,
        duration: 5000
      })
      return
    }

    const openInquiry = () => {
      const whatsapp = helpSettings?.contact?.socialMedia?.whatsapp
      const url = typeof window !== 'undefined' ? window.location.href : ''
      const selections = Object.entries(selectedVariants || {})
        .filter(([, v]) => v)
        .map(([type, v]) => `${type}: ${v?.name || v?.sku || ''}`.trim())
        .filter(Boolean)
      const selectionText = selections.length ? ` (${selections.join(', ')})` : ''
      const text = `Hola! Quiero consultar por el producto: ${product?.name || 'Producto'}${selectionText}. Link: ${url}`
      const href = buildWhatsAppLinkAR(whatsapp, text)
      if (href) {
        window.open(href, '_blank', 'noreferrer')
        return
      }
      navigate(outOfStockPolicy.inquiryChannel === 'help_page' ? '/ayuda' : '/ayuda#contacto')
    }

    if (outOfStockPolicy.purchaseBehavior !== 'allow_purchase') {
      if (availableStock <= 0) {
        if (outOfStockPolicy.purchaseBehavior === 'inquiry_only') {
          openInquiry()
          return
        }
        setNotification({ type: 'error', message: 'Este producto no tiene stock disponible', duration: 5000 })
        return
      }
      if (availableStock < quantity) {
        setNotification({ type: 'error', message: 'No hay suficiente stock disponible', duration: 5000 })
        return
      }
    }

    // Obtener la variante seleccionada (si hay variantes, tomar la primera)
    // Agregar al carrito usando el contexto
    addToCart(product, selectedVariant, quantity)

    // Mostrar notificación de éxito
    setNotification({
      type: 'success',
      message: `Agregaste ${quantity} × ${product.name} al carrito`,
      duration: 6000,
      action: { label: 'Ver carrito', onClick: () => navigate('/carrito') }
    })
  }

  // Obtener productos relacionados (misma categoría, excluyendo el actual)
  const { products: relatedProducts } = useProducts(
    { categoryId: product?.categoryId || null },
    [product?.categoryId]
  )

  const filteredRelatedProducts = useMemo(() => {
    if (!relatedProducts || !product) return []
    const base = relatedProducts.filter(p => p.id !== product.id && p.active !== false)
    const filtered = outOfStockPolicy.visibility === 'hide'
      ? base.filter(p => (p?.stock || 0) > 0)
      : base
    return filtered.slice(0, 4)
  }, [outOfStockPolicy.visibility, relatedProducts, product])

  const fallbackImages = Array.isArray(product?.images)
    ? product.images.map(resolveImageSource).filter(Boolean)
    : []

  const allVariantImages = Array.isArray(product?.variants)
    ? product.variants.flatMap(variant => (variant.images || []).map(resolveImageSource)).filter(Boolean)
    : []

  const selectedVariantImages = selectedVariant
    ? (selectedVariant.images || []).map(resolveImageSource).filter(Boolean)
    : []

  const allVariantVideos = Array.isArray(product?.variants)
    ? product.variants.flatMap(variant => Array.isArray(variant.videos) ? variant.videos : []).map(buildEmbedUrl).filter(Boolean)
    : []

  const selectedVariantVideos = selectedVariant && Array.isArray(selectedVariant.videos)
    ? selectedVariant.videos.map(buildEmbedUrl).filter(Boolean)
    : []

  const productImages = selectedVariantImages.length > 0
    ? selectedVariantImages
    : (allVariantImages.length > 0 ? allVariantImages : fallbackImages)

  const productVideos = selectedVariantVideos.length > 0 ? selectedVariantVideos : allVariantVideos

  const productMedia = useMemo(() => {
    const items = []
    productImages.forEach(src => items.push({ type: 'image', src }))
    productVideos.forEach(src => items.push({ type: 'video', src }))
    return items.length > 0 ? items : [{ type: 'image', src: getImageUrl(null) }]
  }, [productImages, productVideos])

  const activeMedia = productMedia[activeImageIndex] || productMedia[0] || null
  const activeMediaUrl = activeMedia?.type === 'image'
    ? getImageUrl(activeMedia.src, { width: 1200, height: 900, crop: 'fill' })
    : activeMedia?.src

  useEffect(() => {
    setActiveImageIndex(0)
  }, [productMedia])

  // Estados de carga y error
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
        <div className="flex flex-col items-center">
           <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-r-transparent mb-4"></div>
           <p className="text-gray-500">Cargando producto...</p>
        </div>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
        <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Producto no encontrado</h2>
          <p className="text-gray-500 mb-6">{error || 'El producto que buscas no existe o fue eliminado.'}</p>
          <button 
            onClick={() => navigate('/productos')}
            className="bg-primary text-white px-6 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Volver al Catálogo
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen w-full bg-background-light dark:bg-background-dark text-text-dark dark:text-text-light font-sans overflow-x-hidden tracking-tight">
      
      {/* Toast de notificación */}
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'success'}
        duration={notification?.duration ?? 3000}
        actionLabel={notification?.action?.label}
        onAction={notification?.action?.onClick}
        onClose={() => setNotification(null)}
      />

      <main className="flex-1 w-full mx-auto max-w-[1280px] px-4 md:px-10 pb-20">
        
        {/* Breadcrumbs */}
        <div className="py-6">
          <nav aria-label="Breadcrumb" className="flex text-sm text-gray-500 font-light tracking-tight">
            <ol className="inline-flex items-center space-x-1 md:space-x-3">
              <li className="inline-flex items-center">
                <Link to="/" className="hover:text-primary transition-colors">Inicio</Link>
              </li>
              <li><span className="mx-2 text-gray-300">/</span></li>
              <li className="inline-flex items-center">
                <Link to="/productos" className="hover:text-primary transition-colors">Productos</Link>
              </li>
              {productCategory && (
                <>
                  <li><span className="mx-2 text-gray-300">/</span></li>
                  <li className="inline-flex items-center">
                    <Link to={`/productos?categoria=${productCategory.id}`} className="hover:text-primary transition-colors">
                      {productCategory.name}
                    </Link>
                  </li>
                </>
              )}
              <li><span className="mx-2 text-gray-300">/</span></li>
              <li aria-current="page">
                <span className="text-text-dark dark:text-text-light font-normal">{product.name}</span>
              </li>
            </ol>
          </nav>
        </div>

        {/* Sección Principal (Galería + Info) */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mb-20">
          
          {/* Galería de Imágenes */}
          <div className="flex flex-col gap-4">
            <div className="w-full aspect-[4/3] rounded-xl overflow-hidden bg-gray-50 relative group border border-gray-100 dark:border-gray-800">
              {activeMedia?.type === 'video' ? (
                <iframe
                  src={activeMediaUrl}
                  title={`Video ${activeImageIndex + 1}`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <div
                  className="w-full h-full bg-cover bg-center transition-all duration-300"
                  style={{ backgroundImage: `url("${activeMediaUrl}")` }}
                ></div>
              )}
              <div className="absolute top-4 right-4 bg-white/80 dark:bg-black/50 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <span className="material-symbols-outlined text-gray-600 dark:text-gray-200 text-[20px]">zoom_in</span>
              </div>
            </div>
            
            {productMedia.length > 1 && (
              <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                {productMedia.map((item, index) => (
                  <button
                    key={`${item.type}-${item.src}-${index}`}
                    onClick={() => setActiveImageIndex(index)}
                    className={`relative w-24 aspect-square rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${
                      activeImageIndex === index
                        ? 'border-primary'
                        : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600 opacity-70 hover:opacity-100'
                    }`}
                  >
                    {item.type === 'video' ? (
                      <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-200 dark:from-gray-800 dark:to-gray-700 flex flex-col items-center justify-center gap-2 text-gray-700 dark:text-gray-100">
                        <div className="size-9 rounded-full bg-white/90 dark:bg-gray-900/80 shadow-sm flex items-center justify-center border border-gray-200 dark:border-gray-700">
                          <span className="material-symbols-outlined text-[18px]">play_arrow</span>
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.2em]">Video</span>
                      </div>
                    ) : (
                      <div
                        className="w-full h-full bg-cover bg-center"
                        style={{ backgroundImage: `url("${getImageUrl(item.src, { width: 200, height: 200, crop: 'fill' })}")` }}
                      ></div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Información del Producto */}
          <div className="flex flex-col h-full">
            <div className="border-b border-gray-100 dark:border-gray-800 pb-6 mb-6">
              <div className="flex flex-col gap-2 mb-4">
                <div className="flex justify-between items-start">
                  {selectedVariant?.sku && (
                    <span className="text-xs uppercase tracking-widest text-gray-500 font-medium">SKU: {selectedVariant.sku}</span>
                  )}
                  {availableStock > 0 ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-100 dark:border-green-800">
                      <span className="size-1.5 rounded-full bg-green-600 dark:bg-green-400"></span>
                      En Stock
                    </span>
                  ) : (
                     <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-100 dark:border-red-800">
                      <span className="size-1.5 rounded-full bg-red-600 dark:bg-red-400"></span>
                      Agotado
                    </span>
                  )}
                </div>
                <h1 className="text-3xl md:text-4xl font-medium text-text-dark dark:text-text-light tracking-tight text-balance">
                  {product.name}
                </h1>
              </div>
              
              <div className="flex items-end gap-4 mb-6">
                <span className="text-3xl font-semibold text-primary tracking-tight">
                  {formatCurrency(finalPrice)}
                </span>
                {product.specialPrice && product.specialPrice < product.price && (
                   <span className="text-lg text-gray-400 line-through mb-1">
                     {formatCurrency(calculateProductFinalPrice(product, fullCategory))}
                   </span>
                )}
              </div>
              
              {product.shortDescription && (
                <p className="text-gray-600 dark:text-gray-300 font-light leading-relaxed tracking-tight text-lg mb-6">
                  {product.shortDescription}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-6">
              
              {/* Variantes */}
              {Object.keys(variantsByType).length > 0 && (
                <div className="flex flex-col gap-4">
                  {Object.entries(variantsByType).map(([type, variants]) => (
                    <div key={type} className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-text-dark dark:text-text-light capitalize">
                        {type === 'color' ? 'Color' : type === 'size' ? 'Tamaño' : type === 'material' ? 'Material' : type}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {variants.map((variant, index) => {
                          const isSelected = selectedVariants[type]?.name === variant.name
                          const isOutOfStock = variant.stock === 0 || variant.stock === null
                          
                          if (type === 'color') {
                            return (
                              <button
                                key={index}
                                onClick={() => !isOutOfStock && handleVariantSelect(type, variant)}
                                disabled={isOutOfStock}
                                className={`relative w-10 h-10 rounded-full border-2 transition-all ${
                                  isSelected
                                    ? 'border-primary ring-4 ring-primary/25 scale-110 shadow-sm'
                                    : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                                } ${isOutOfStock ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                                style={{ backgroundColor: variant.name }}
                                title={`${variant.name}${variant.price ? ` (+${formatCurrency(variant.price)})` : ''}`}
                              />
                            )
                          }
                          
                          return (
                            <button
                              key={index}
                              onClick={() => !isOutOfStock && handleVariantSelect(type, variant)}
                              disabled={isOutOfStock}
                              className={`px-4 py-2 rounded-lg text-sm border transition-all ${
                                isSelected 
                                  ? 'border-primary bg-primary text-white font-medium ring-2 ring-primary/25 shadow-sm' 
                                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                              } ${isOutOfStock ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800' : ''}`}
                            >
                              {variant.name}
                              {variant.price && variant.price > 0 && (
                                <span className="ml-1 text-xs opacity-80">+{formatCurrency(variant.price)}</span>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Selector de Cantidad */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-text-dark dark:text-text-light">Cantidad</label>
                  <div className="flex items-center">
                    <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg h-12">
                      <button 
                        onClick={() => handleQuantityChange(-1)}
                        disabled={quantity <= 1}
                        className="w-12 h-full flex items-center justify-center text-gray-500 hover:text-primary transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 rounded-l-lg disabled:opacity-30"
                      >
                        <span className="material-symbols-outlined text-[20px]">remove</span>
                      </button>
                      <input 
                        className="w-16 h-full text-center border-none focus:ring-0 p-0 text-text-dark dark:text-text-light bg-transparent font-medium" 
                        min="1" 
                        readOnly 
                        type="number" 
                        value={quantity}
                      />
                      <button 
                        onClick={() => handleQuantityChange(1)}
                        disabled={outOfStockPolicy.purchaseBehavior !== 'allow_purchase' && quantity >= availableStock}
                        className="w-12 h-full flex items-center justify-center text-gray-500 hover:text-primary transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 rounded-r-lg disabled:opacity-30"
                      >
                        <span className="material-symbols-outlined text-[20px]">add</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botones de Acción */}
              <div className="flex flex-col sm:flex-row gap-4 mt-2">
                <div className="flex-1 flex flex-col gap-2">
                  <button 
                    onClick={handleAddToCart}
                    disabled={availableStock === 0 && outOfStockPolicy.purchaseBehavior === 'block_purchase'}
                    className="w-full bg-primary hover:bg-primary/90 text-white h-12 rounded-lg font-medium tracking-tight transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="material-symbols-outlined text-[20px]">shopping_cart</span>
                    {availableStock === 0 && outOfStockPolicy.purchaseBehavior === 'inquiry_only'
                      ? 'Consultar'
                      : 'Agregar al Carrito'}
                  </button>
                  {getCartItemCount() > 0 ? (
                    <button
                      type="button"
                      onClick={() => navigate('/carrito')}
                      className="w-full h-11 rounded-lg font-medium tracking-tight transition-all border border-primary/30 text-primary hover:bg-primary/10 flex items-center justify-center gap-2"
                    >
                      <span className="material-symbols-outlined text-[20px]">shopping_bag</span>
                      Ver carrito
                    </button>
                  ) : null}
                </div>
                <button 
                  className="flex-1 bg-accent-light dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-text-dark dark:text-text-light border border-transparent h-12 rounded-lg font-medium tracking-tight transition-all flex items-center justify-center"
                >
                  Comprar Ahora
                </button>
              </div>

              {/* Info Box */}
              <div className="flex flex-col gap-3 mt-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-100 dark:border-gray-800">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-gray-400 mt-0.5">local_shipping</span>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-text-dark dark:text-text-light">Envío Estándar</span>
                    <span className="text-xs text-gray-500">Calculado en el checkout (aprox. 3-5 días)</span>
                  </div>
                  <span className="ml-auto text-sm font-medium text-text-dark dark:text-text-light">
                    {finalPrice > 50000 ? 'Gratis' : 'Por calcular'}
                  </span>
                </div>
                <div className="w-full h-px bg-gray-200 dark:bg-gray-700"></div>
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-gray-400 mt-0.5">verified_user</span>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-text-dark dark:text-text-light">Garantía de Calidad</span>
                    <span className="text-xs text-gray-500">Cobertura completa por defectos de fábrica</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sección de Detalles (Descripción y Especificaciones) */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-10 border-t border-gray-100 dark:border-gray-800 pt-16 mb-20">
          
          {/* Descripción Larga */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <h3 className="text-2xl font-medium text-text-dark dark:text-text-light tracking-tight">Descripción</h3>
            <div className="prose prose-slate prose-lg text-gray-600 dark:text-gray-300 font-light leading-relaxed max-w-none">
              {product.description ? (
                <div dangerouslySetInnerHTML={{ __html: product.description }} />
              ) : (
                <p>No hay descripción disponible para este producto.</p>
              )}
            </div>
          </div>

          {/* Especificaciones */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <h3 className="text-2xl font-medium text-text-dark dark:text-text-light tracking-tight">Especificaciones</h3>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              
              {product.material && (
                <div className="grid grid-cols-2 py-4">
                  <span className="text-gray-500 text-sm">Material</span>
                  <span className="text-text-dark dark:text-text-light text-sm font-medium text-right">{product.material}</span>
                </div>
              )}
              
              {product.dimensions && (
                <div className="grid grid-cols-2 py-4">
                  <span className="text-gray-500 text-sm">Dimensiones</span>
                  <span className="text-text-dark dark:text-text-light text-sm font-medium text-right">
                    {[
                      product.dimensions.width ? `${product.dimensions.width}cm` : '',
                      product.dimensions.height ? `${product.dimensions.height}cm` : '',
                      product.dimensions.depth ? `${product.dimensions.depth}cm` : ''
                    ].filter(Boolean).join(' x ')}
                  </span>
                </div>
              )}

              {product.brand && (
                <div className="grid grid-cols-2 py-4">
                  <span className="text-gray-500 text-sm">Marca</span>
                  <span className="text-text-dark dark:text-text-light text-sm font-medium text-right">{product.brand}</span>
                </div>
              )}

              {selectedVariant?.sku && (
                <div className="grid grid-cols-2 py-4">
                  <span className="text-gray-500 text-sm">Modelo</span>
                  <span className="text-text-dark dark:text-text-light text-sm font-medium text-right">{selectedVariant.sku}</span>
                </div>
              )}
              
              {fullCategory && (
                <div className="grid grid-cols-2 py-4">
                  <span className="text-gray-500 text-sm">Categoría</span>
                  <span className="text-text-dark dark:text-text-light text-sm font-medium text-right">{fullCategory.name}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Productos Relacionados */}
        {filteredRelatedProducts.length > 0 && (
          <section className="flex flex-col gap-8">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-medium text-text-dark dark:text-text-light tracking-tight">Completa el Look</h3>
              <Link to={`/productos?categoria=${product.categoryId}`} className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                Ver colección completa
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
              {filteredRelatedProducts.map(relatedProduct => {
                 const hasDiscount = relatedProduct.specialPrice && relatedProduct.specialPrice < relatedProduct.price;
                 const discountPercentage = hasDiscount 
                   ? Math.round(((relatedProduct.price - relatedProduct.specialPrice) / relatedProduct.price) * 100)
                   : 0;
                 const relatedImages = getVariantImages(relatedProduct, { width: 600, height: 800, crop: 'fill' })
                 const relatedCover = relatedImages[0] || 'https://placehold.co/600x400?text=No+Image'

                 return (
              <Link 
                key={relatedProduct.id} 
                to={`/productos/${relatedProduct.id}`}
                className="flex flex-col gap-3 group cursor-pointer"
              >
                    <div className="w-full aspect-[4/5] overflow-hidden rounded-xl relative bg-gray-50 border border-gray-100 dark:border-gray-800">
                      {hasDiscount && (
                        <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-semibold px-2 py-1 rounded tracking-wide uppercase z-20">
                          -{discountPercentage}%
                        </div>
                      )}
                      
                      <div 
                        className="w-full h-full bg-cover bg-center transition-transform duration-500 group-hover:scale-105" 
                        style={{ 
                          backgroundImage: `url("${relatedCover}")` 
                        }}
                      ></div>
                      
                      <button className="absolute bottom-3 right-3 size-10 bg-white dark:bg-gray-800 rounded-full shadow-sm flex items-center justify-center text-text-dark dark:text-text-light opacity-0 group-hover:opacity-100 transition-all hover:bg-primary hover:text-white translate-y-2 group-hover:translate-y-0 z-20">
                        <span className="material-symbols-outlined text-[20px]">add_shopping_cart</span>
                      </button>
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      <div className="flex justify-between items-start">
                        <p className="text-text-dark dark:text-text-light text-lg font-medium leading-tight group-hover:text-primary transition-colors tracking-tight truncate pr-2">
                          {relatedProduct.name}
                        </p>
                        <p className="text-primary font-semibold text-lg tracking-tight whitespace-nowrap">
                          {hasDiscount ? formatCurrency(relatedProduct.specialPrice) : formatCurrency(relatedProduct.price)}
                        </p>
                      </div>
                      {hasDiscount && (
                        <p className="text-xs text-gray-400 line-through text-right">
                          {formatCurrency(relatedProduct.price)}
                        </p>
                      )}
                    </div>
                  </Link>
                )
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  )
}

export default ProductDetail

